<template>
  <div class="resume-wrapper" :class="{ 'resume-wrapper--videoresume': isVideoresume }">
    <div class="resume-content">
      <div class="actions">
        <label
          v-tooltip="{
            content: $tr('vacancy.openRegistration.resumeTooltip'),
            placement: 'right',
            classes: 'tight'
          }"
          class="upload-icon"
          for="resumeFile"
        >
          <img :src="resumeImage" />
        </label>
      </div>

      <div class="resume-content-input">
        <input
          id="resumeLink"
          v-model="resumeUrl"
          class="resume-input"
          :class="{ error: urlError }"
          type="text"
          :placeholder="resumeFilePlaceholder"
          @input="uploadResumeUrl"
        />

        <div v-if="urlError" class="resume-link-error">{{ $tr('vacancy.openRegistration.resumeUrlIncorrect') }}</div>

        <div v-if="fileName" class="file-name">
          <span>{{ fileName || '' }}</span>

          <div class="file-name-icon" @click="resetFile">
            <img src="/static/images/elements/reset-file.svg" />
          </div>
        </div>
      </div>
    </div>

    <input
      id="resumeFile"
      ref="fileSelector"
      v-validate="{
        required: true,
        size: 5000,
        ext: ['doc', 'docx', 'pdf']
      }"
      type="file"
      accept="application/msword, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      :class="{
        error: errors.has('resumeFile'),
        valid: fields.resumeFile ? fields.resumeFile.valid : null
      }"
      name="resumeFile"
      :disabled="isFinished"
      @change="uploadResume($event.target.files)"
    />

    <label v-if="errors.has('resumeFile')" class="helper">
      {{ errors.first('resumeFile') }}
    </label>
  </div>
</template>

<script>
const pattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
    '(\\#[-a-z\\d_]*)?$','i');

export default {
  name: 'ResumeUpload',

  props: ['item', 'isVideoresume', 'isFinished'],

  data() {
    return {
      file: null,
      resumeFile: null,
      url: null
    };
  },

  computed: {
    fileName: {
      get() {
        if (this.file === null) {
          return this.item.resumeName ? this.item.resumeName.replace(/^.*[\\\/]/, '') : '';
        }

        return this.file;
      },

      set(val) {
        this.file = val;
      }
    },

    resumeUrl: {
      get() {
        if (this.url === null) {
          return this.item.resumeUrl || null;
        }

        return this.url;
      },

      set(val) {
        this.url = val.trim();
      }
    },

    urlError() {
      if (!this.resumeUrl) {
        return false;
      }

      return !pattern.test(this.resumeUrl);
    },

    resumeImage() {
      if (this.isVideoresume) {
        return '/static/images/elements/upload-file-b2c.svg';
      }

      return this.isFinished ? '/static/icons/landing_ok_2.svg' : '/static/images/elements/upload-file.svg';
    },

    resumeFilePlaceholder() {
      if (this.isVideoresume) {
        if (!this.resumeFile) {
          return this.$tr('vacancy.openRegistrationVideoresume.resumePlaceholder');
        } else {
          return this.$tr('vacancy.openRegistrationVideoresume.resumePlaceholderWithFile');
        }
      }
      return !this.resumeFile
        ? this.$tr('vacancy.openRegistration.resumePlaceholder')
        : this.$tr('vacancy.openRegistration.resumePlaceholderWithFile');
    }
  },

  watch: {
    urlError: {
      immediate: true,
      handler(val) {
        this.$emit('setError', val);
      }
    },

    fileName: {
      immediate: true,
      handler(val) {
        this.$emit('setResumeName', !!val);
      }
    },

    resumeUrl: {
      immediate: true,
      handler(val) {
        this.$emit('uploadResumeUrl', val ? val.trim() : '');
      }
    }
  },

  methods: {
    uploadResume(files) {
      if (!files.length) {
        return;
      }

      this.fileName = files[0].name;
      this.resumeFile = files[0];
      this.$emit('upload', this.resumeFile);
    },

    uploadResumeUrl() {
      this.$emit('uploadResumeUrl', this.resumeUrl);
    },

    resetFile() {
      this.resumeFile = null;
      this.fileName = '';

      // This allows user to re-select
      // same file after reset
      // see for more details:
      // https://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
      // https://stackoverflow.com/questions/20549241/how-to-reset-input-type-file
      this.$refs.fileSelector.value = '';
      if (!/safari/i.test(navigator.userAgent)) {
        this.$refs.fileSelector.type = '';
        this.$refs.fileSelector.type = 'file';
      }
      this.$emit('reset');
    }
  }
};
</script>

<style lang="scss" scoped>
.resume-wrapper {
  position: relative;
  font-size: 15px;
  line-height: 24px;
}

input[type='text'] {
  border: 1px solid #d2d6e0;
  opacity: 1;
}

input[type='file'] {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;

  + .actions {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 40px;
    box-sizing: border-box;

    span {
      cursor: pointer;
      font-size: 15px;
      position: absolute;
      top: 0;
      right: 40px;
      height: 100%;
      display: flex;
      align-items: center;
      z-index: 3;
    }

    label {
      cursor: pointer;
      margin-left: 0;
      position: absolute;
      top: 0;
      left: 0;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-right: 10px;
    }
  }
}
input.error + input[type='file'] {
  + label .icon-upload {
    color: $red-color;
  }
}

.resume-title {
  font-size: 15px;
  line-height: 24px;
  margin-bottom: 1rem;
}

.resume-content {
  display: flex;
  align-items: flex-start;
  max-width: 100%;
}

.resume-input {
  flex: 1;
}

.actions {
  width: 24px;
  height: 40px;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon {
  display: flex;
  cursor: pointer;
}

.resume-content-input {
  flex: 1;
  max-width: calc(100% - 2rem);
}

.resume-link-error {
  font-size: 12px;
  line-height: 16px;
  color: #b00020;
  margin-bottom: 4px;
}

.file-name {
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  max-width: 100%;

  span {
    max-width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    box-sizing: border-box;
    display: inline-block;
  }
}

.file-name-icon {
  cursor: pointer;
  margin-left: 6px;
  margin-top: 2px;
}

.resume-wrapper--videoresume {
  &.resume-wrapper {
    .resume-content {
      .resume-input {
        // font-family: 'Euclid Circular A';
        font-style: normal;
        font-weight: normal;
        font-size: 15px;
        line-height: 24px;
        color: #8d8d8d;
        background: #ffffff;
        border: 1px solid #d1d8e0;
        box-sizing: border-box;
        border-radius: 4px;
        padding: 16px;
        height: 56px;
        min-height: 56px;
        max-height: 56px;

        &::placeholder {
          color: #c4b9d3;
        }
      }
    }

    .actions {
      height: 56px;
    }
  }
}
</style>
